import { useState, useEffect, useRef } from 'react';

const voices = [
  {
    title: 'IT未経験の私でも不安なくスタートできた1日の流れ',
    year: '2025年度入社',
    gender: 'female',
    image: '/image/recruit-people1.jpg',
    content:
      '未経験で入社しても、一日の業務サイクルが明確で、先輩方がしっかりサポートしてくれるので、安心して仕事に取り組めます。朝会でタスクを確認し、作業タイムでは焦らず自分のペースで進められます。相談・レビューを通して実践的に学び、就業前に進捗を共有。未経験でも無理なく業務を進められる仕組みが整っています。',
  },
  {
    title: 'IT初心者でも安心して働ける職場',
    year: '2024年度入社',
    gender: 'female',
    image: '/image/recruit-people2.jpg',
    content:
      'スキルを伸ばすためのサポートが充実していると思います。入社前や入社後の研修があり、毎月目標設定やフィードバックの機会が設けられているので、初めてITに触れる人でも自分のペースで成長していくことができると思います。分からないことや悩みがあったときにすぐに相談しやすい環境なので、非常に働きやすいです。',
  },
  {
    title: '日々成長出来る環境',
    year: '2024年度入社',
    gender: 'male',
    image: '/image/recruit-people3.jpg',
    content:
      '私は専門学校から新卒として入社しました。学業と実際の業務の違いを感じ、自分の力不足を痛感することもあります。しかし、先輩からのフォローや定期的な面談、勉強会など、自己成長に専念できる環境が整っており、業務を通じて自分の成長を実感しています。',
  },
  {
    title: '充実のサポート体制',
    year: '2022年度入社',
    gender: 'female',
    image: '/image/recruit-people4.jpg',
    content:
      '入社3年目でリモートワークを行っています。今期から現場が変わり不安な気持ちもありましたが、定期的な面談や社内交流があるため、不安を感じることなく業務に取り掛かれています。また、食費補助など福利厚生が充実しており、有給休暇も取得しやすいため、とても働きやすい環境です。',
  },
  {
    title: '中途採用からグループ長へ',
    year: '2020年度中途入社',
    gender: 'male',
    image: '/image/recruit-people5.jpg',
    content:
      '中途採用で入社してから、さまざまな現場を渡り歩いてきました。リーダーとしてチームメンバーの管理やプロジェクトを遂行し、顧客からの信頼も得ることができました。そして現在では、複数のリーダーを管轄するグループ長を任されるまでになりました。成果が目に見える形で感じられる、とてもやりがいのある職場です。',
  },
  {
    title: '仕事とプライベート',
    year: '2015年度入社',
    gender: 'male',
    image: '/image/recruit-people6.jpg',
    content:
      '職場によりますが、有給休暇が取りやすいため、仕事とプライベートのバランスを取りながら業務に従事できる環境が多くあります。また、資格取得が評価され、昇進や昇給の機会が広がるため、モチベーションにつながります。さらに、福利厚生も年々充実しており、働きやすい環境が整いつつあります。',
  },
];

const VoiceCard = ({
  voice,
  index,
  expandedIndex,
  onToggle,
}: {
  voice: (typeof voices)[0];
  index: number;
  expandedIndex: number | null;
  onToggle: (i: number) => void;
}) => (
  <article className="bg-white rounded-xl p-8 shadow-[0_2px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_32px_rgba(137,181,107,0.18)] transition-all duration-500 group hover:-translate-y-1 h-full flex flex-col">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-14 h-14 rounded-full bg-[#89B56B]/10 flex items-center justify-center flex-shrink-0 overflow-hidden group-hover:scale-110 transition-transform duration-300">
        <img
          src={voice.image}
          alt="社員"
          className="w-full h-full object-cover"
        />
      </div>
      <div>
        <p className="text-xs text-[#89B56B] font-medium">{voice.year}</p>
      </div>
    </div>

    <h3 className="text-base font-bold text-[#89B56B] mb-4 leading-relaxed transition-colors">
      {voice.title}
    </h3>

    <p
      className={`text-sm text-gray-500 leading-[1.9] flex-1 ${
        expandedIndex === index ? '' : 'line-clamp-4'
      }`}
    >
      {voice.content}
    </p>

    {voice.content.length > 100 && (
      <button
        onClick={() => onToggle(index)}
        className="mt-3 text-xs text-[#89B56B] font-medium hover:text-[#5DAA68] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
      >
        {expandedIndex === index ? '閉じる' : '続きを読む'}
        <i
          className={`ri-arrow-down-s-line transition-transform duration-300 ${
            expandedIndex === index ? 'rotate-180' : ''
          }`}
        ></i>
      </button>
    )}
  </article>
);

const RecruitVoices = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<boolean[]>(
    new Array(voices.length).fill(false)
  );

  // Mobile slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          voices.forEach((_, i) => {
            setTimeout(() => {
              setVisibleCards((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 120);
          });
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleExpand = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setExpandedIndex(null);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0 && currentSlide < voices.length - 1) {
        goToSlide(currentSlide + 1);
      } else if (diff < 0 && currentSlide > 0) {
        goToSlide(currentSlide - 1);
      }
    }
  };

  return (
    <section
      className="py-16 bg-white relative"
      id="voices"
      ref={sectionRef}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-8 left-0 text-[80px] sm:text-[180px] font-bold text-[#89B56B]/5 leading-none select-none tracking-tight anim-bg-text ${
            isVisible ? 'is-visible' : ''
          }`}
        >
          People
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div
          className={`text-center mb-16 anim-fade-up ${
            isVisible ? 'is-visible' : ''
          }`}
        >
          <p className="text-[#89B56B] text-xs font-semibold tracking-[0.25em] uppercase mb-4">
            People
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#89B56B]">
            社員の声
          </h2>
        </div>

        {/* ── PC: grid layout ── */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {voices.map((voice, index) => (
            <div
              key={index}
              className={`anim-fade-up ${visibleCards[index] ? 'is-visible' : ''}`}
            >
              <VoiceCard
                voice={voice}
                index={index}
                expandedIndex={expandedIndex}
                onToggle={toggleExpand}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Mobile: horizontal slider (コンテナ外・画面幅いっぱい) ── */}
      <div className="md:hidden mt-6">
        <div
          className="relative w-full overflow-hidden py-4"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{
              transform: `translateX(calc(-${currentSlide * 100}% - ${currentSlide * 0}px))`,
            }}
          >
            {voices.map((voice, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-full px-6 box-border"
              >
                <div className="bg-white rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)]">
                  <VoiceCard
                    voice={voice}
                    index={index}
                    expandedIndex={expandedIndex}
                    onToggle={toggleExpand}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {voices.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                currentSlide === index
                  ? 'w-6 h-2 bg-[#89B56B]'
                  : 'w-2 h-2 bg-[#89B56B]/30'
              }`}
              aria-label={`スライド ${index + 1}`}
            />
          ))}
        </div>

        {/* Prev / Next arrows */}
        <div className="flex items-center justify-center mt-4 gap-8 w-full">
          <button
            onClick={() => goToSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-[#89B56B]/40 text-[#89B56B] disabled:opacity-30 cursor-pointer transition-colors hover:bg-[#89B56B]/10"
            aria-label="前へ"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <span className="text-xs text-gray-400">
            {currentSlide + 1} / {voices.length}
          </span>
          <button
            onClick={() => goToSlide(Math.min(voices.length - 1, currentSlide + 1))}
            disabled={currentSlide === voices.length - 1}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-[#89B56B]/40 text-[#89B56B] disabled:opacity-30 cursor-pointer transition-colors hover:bg-[#89B56B]/10"
            aria-label="次へ"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default RecruitVoices;
